import { Dispatch, FC, SetStateAction, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { AcresFeatureFlags } from '@/constants/constants';
import { useDeedPlotterContext } from '@/features/boundaries/deedPlotter/provider';
import { useCustomizeLandContext } from '@/features/customizeMyLand/provider';
import { useGetFeatureFlag } from '@/hooks/useGetFeatureFlag';
import { useFileManagementContext } from '@/providers/FileManagementProvider';
import { RootState } from '@/redux/store';

import { Footer } from './footer';
import { Header } from './header';
import { SelectionsContainer } from './selectionsContainer';

type Props = {
  isSelectionsPanelExpanded: boolean;
  setIsSelectionsPanelExpanded: Dispatch<SetStateAction<boolean>>;
  formattedTotalAcres: string;
};

export const SelectionsPanelContent: FC<Props> = ({
  isSelectionsPanelExpanded,
  setIsSelectionsPanelExpanded,
  formattedTotalAcres,
}) => {
  const isNewSelectionsPanelActive = useGetFeatureFlag(
    AcresFeatureFlags.TEMP_NEW_SELECTIONS_PANEL
  );

  const { activeFile } = useFileManagementContext();

  const allFeatures = useSelector(
    (state: RootState) => state.features.allFeatures
  );

  const {
    customizations: { allCustomizations },
  } = useCustomizeLandContext();

  const { validGroups: filterGroups } = useDeedPlotterContext();

  useEffect(
    () => {
      if (
        (allFeatures.length > 0 ||
          allCustomizations.length > 0 ||
          filterGroups.length > 0) &&
        !isSelectionsPanelExpanded
      ) {
        if (isNewSelectionsPanelActive) return;
        setIsSelectionsPanelExpanded(true);
      } else if (
        allFeatures.length === 0 &&
        isSelectionsPanelExpanded &&
        allCustomizations.length === 0 &&
        filterGroups.length === 0 &&
        !activeFile
      ) {
        if (isNewSelectionsPanelActive) return;
        setIsSelectionsPanelExpanded(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activeFile,
      allFeatures.length,
      allCustomizations.length,
      filterGroups.length,
      setIsSelectionsPanelExpanded,
      isNewSelectionsPanelActive,
    ]
  );

  return (
    <div
      className={twMerge(
        'absolute right-0 flex w-full transform flex-col bg-white transition-all duration-300 scrollbar-hide',
        'max-h-[calc(100vh_-_40px_-_48px_-_40px_)] sm:max-h-[calc(100vh_-_48px_-_40px)]', // 40px is the height of the top nav bar
        isSelectionsPanelExpanded
          ? 'visible opacity-100'
          : 'invisible opacity-0'
      )}
    >
      <Header formattedTotalAcres={formattedTotalAcres} />
      <SelectionsContainer />
      <Footer />
    </div>
  );
};
